function findMatchingPart(existingParts, incomingPart, index) {
  if (!Array.isArray(existingParts) || existingParts.length === 0) {
    return null;
  }
  if (incomingPart.id) {
    const byId = existingParts.find((p) => p.id === incomingPart.id);
    return byId || null;
  }
  if (incomingPart.toolCallId) {
    const byCall = existingParts.find((p) => p.toolCallId === incomingPart.toolCallId);
    return byCall || null;
  }
  if (index < existingParts.length && existingParts[index]?.type === incomingPart.type) {
    return existingParts[index];
  }
  // No toolName-only fallback: a turn can contain several calls to the same tool, and matching
  // by name alone would copy one call's persisted output onto another. Parts without a stable
  // id/toolCallId and a matching positional slot are treated as new.
  return null;
}

/**
 * Called only when the persisted part has newer sequence coverage than the frame.
 */
function mergeStreamPart(storePart, streamPart) {
  if (streamPart.type === "text" || streamPart.type === "reasoning") {
    return storePart;
  }
  if (streamPart.type !== "tool") {
    return streamPart;
  }

  const storeOutput = storePart.output;
  if ((storeOutput === undefined || storeOutput === null) && storePart.state !== "output-error") {
    return streamPart;
  }
  return {
    ...streamPart,
    output: storePart.output,
    state: storePart.state ?? streamPart.state,
    errorText: storePart.errorText ?? streamPart.errorText,
  };
}

/**
 * Merge a streamed assistant turn with the copy already in the store so
 * message_added updates are not clobbered by throttled ui_chunk flushes.
 */
export function mergeStreamTurnWithStore(existingMessage, incomingTurn) {
  const storeWins = existingMessage?._streamSource === "persisted" &&
    Number.isFinite(existingMessage._streamSequence) &&
    existingMessage._streamSequence > (incomingTurn?._streamSequence ?? -1);
  const legacyToolOutput = existingMessage?._streamSource === "persisted" &&
    !Number.isFinite(existingMessage._streamSequence) && !Number.isFinite(incomingTurn?._streamSequence);
  if (!existingMessage?.parts?.length || !incomingTurn?.parts?.length) {
    return storeWins ? existingMessage : incomingTurn;
  }

  const mergedParts = incomingTurn.parts.map((part, index) => {
    const existingPart = findMatchingPart(existingMessage.parts, part, index);
    if (!existingPart) return part;
    // Only an explicitly persisted, newer revision may override this frame.
    return storeWins || (legacyToolOutput && part.type === "tool")
      ? mergeStreamPart(existingPart, part) : part;
  });

  // A throttled working copy can predate newly persisted validation/tool parts.
  // Keep those parts instead of deleting them when the frame finally commits.
  existingMessage.parts.forEach((part, index) => {
    if (!findMatchingPart(incomingTurn.parts, part, index)) mergedParts.push(part);
  });
  // Preserve the winning coverage too, so a second stale frame cannot erase it.
  return storeWins
    ? { ...incomingTurn, ...existingMessage, parts: mergedParts }
    : { ...existingMessage, ...incomingTurn, parts: mergedParts };
}
